import { LineItem, RevenuePeriod } from '@vendasta/sales-orders';
import { Injectable } from '@angular/core';
import { Frequency } from '@vendasta/galaxy/frequency';
import { TaxOptions } from '@vendasta/sales-orders/lib/_internal';
import { getTaxAmountOfLineItemForRevenuePeriodPrice } from '../edit-order/revenue-utils';

//converts frequency to monthly
const frequencyConversionValue = {
  [RevenuePeriod.ONETIME]: 0,
  [RevenuePeriod.MONTHLY]: 1,
  [RevenuePeriod.YEARLY]: 12,
};
import { getLineItemsDueNow } from '../../shared/utils';

const convertRevenuePeriodToFrequency = {
  [RevenuePeriod.ONETIME]: Frequency.ONE_TIME,
  [RevenuePeriod.MONTHLY]: Frequency.MONTHLY,
  [RevenuePeriod.YEARLY]: Frequency.YEARLY,
};

export const FuturePaymentScheduledTime = {
  StartChargingImmediately: 0,
  RenewIndefinitely: -1,
  NoRenewal: 0,
};

export interface FuturePaymentDisplayItemsWithCurrency {
  currencyCode: string;
  lineItems: FuturePaymentDisplayItem[];
}

export interface FuturePaymentDisplayItem {
  startDate: number;
  duration: number;
  price: number;
  frequency: Frequency;
}

interface FuturePaymentItemsMapWithCurrency {
  currencyCode: string;
  lineItemsMap: lineItemsMapByDateAndDuration;
}

interface lineItemsMapByDateAndDuration {
  [startDate: number]: {
    [duration: number]: {
      [period in RevenuePeriod]?: number;
    };
  };
}

@Injectable()
export class RetailSummaryService {
  public constructFuturePaymentLineItemsArray(
    lineItems: LineItem[],
    taxOptions: TaxOptions[],
  ): FuturePaymentDisplayItemsWithCurrency {
    if (!lineItems.length) return {} as FuturePaymentDisplayItemsWithCurrency;
    const { lineItemsMap, currencyCode } = this.calculateLineItemsFuturePayments(lineItems, taxOptions);
    const lineItemsArray: FuturePaymentDisplayItem[] = [];

    /*
      Javascript maps sort the numeric keys in ascending order
      the object entries will always be ordered for the main dictionary
      The middle(duration dictionary) is sorted in ascending order, except -1 (renewing indefinitely)
      inner dictionary (period) is sorted as (onetime, monthly, yearly)
     */
    //Start date dictionary
    Object.entries(lineItemsMap)
      .sort((a, b) => {
        return Number(a[0]) - Number(b[0]);
      })
      .forEach(([startDate, itemsByDuration]: [string, { [_: number]: { [_ in RevenuePeriod]?: number } }]) => {
        //Duration dictionary
        Object.entries(itemsByDuration).forEach(
          ([duration, priceByPeriod]: [string, { [_ in RevenuePeriod]?: number }]) => {
            //Period dictionary
            Object.entries(priceByPeriod).forEach(([period, price]: [string, number]) => {
              lineItemsArray.push({
                startDate: Number(startDate),
                duration: Number(duration),
                price: price,
                frequency: convertRevenuePeriodToFrequency[period],
              } as FuturePaymentDisplayItem);
            });
          },
        );
      });

    return { lineItems: lineItemsArray, currencyCode: currencyCode };
  }

  public calculateLineItemsFuturePayments(
    lineItems: LineItem[],
    taxOptions: TaxOptions[],
  ): FuturePaymentItemsMapWithCurrency {
    if (!lineItems?.length) return {} as FuturePaymentItemsMapWithCurrency;

    const now = new Date();
    const dateInt24Hours = new Date(now.getTime() + 24 * 60 * 60 * 1000);

    const dueNowItems = getLineItemsDueNow(lineItems);

    const currencyCode = lineItems[0].currencyCode;

    const futurePaymentsGrouped: lineItemsMapByDateAndDuration = {};

    lineItems.forEach((lineItem) => {
      const startDate = lineItem.billingPeriod?.startDate ? new Date(lineItem.billingPeriod?.startDate) : undefined;
      const endDate = lineItem.billingPeriod?.endDate ? new Date(lineItem.billingPeriod?.endDate) : undefined;

      const duration = lineItem.billingPeriod?.duration?.value;

      const revenueComponents = lineItem.currentRevenue?.revenueComponents;

      let numericStartDate = startDate?.getTime();

      //If items is due now, set to StartChargingImmediately
      if (!numericStartDate || dueNowItems.includes(lineItem)) {
        numericStartDate = FuturePaymentScheduledTime.StartChargingImmediately;
      }

      if (!futurePaymentsGrouped[numericStartDate]) {
        futurePaymentsGrouped[numericStartDate] = {};
      }

      revenueComponents.forEach((revenueComponent) => {
        const frequency = revenueComponent.period || RevenuePeriod.ONETIME; // one time products and setup fees can return undefined period
        let componentDuration = duration;

        if (!componentDuration && endDate) {
          componentDuration = this.convertEndDateToDuration(startDate || new Date(), endDate, frequency);
        }

        if (frequency === RevenuePeriod.ONETIME) {
          componentDuration = FuturePaymentScheduledTime.NoRenewal;
        } else if (!componentDuration) {
          componentDuration = FuturePaymentScheduledTime.RenewIndefinitely;
        }

        const price = this.calculateLineItemPrice(
          revenueComponent.value,
          lineItem.quantity,
          lineItem.taxRateIds,
          taxOptions,
        );

        //Skip items that are not recurring or are free
        if (!this.isLineItemRecurring(startDate, dateInt24Hours, componentDuration, frequency) || !price) {
          return;
        }

        //Initialize duration dictionary if it doesnt exist
        if (!futurePaymentsGrouped[numericStartDate][componentDuration]) {
          futurePaymentsGrouped[numericStartDate][componentDuration] = {};
        }

        //initialize price if it doesnt exist, add to it otherwise
        if (!futurePaymentsGrouped[numericStartDate][componentDuration][frequency]) {
          futurePaymentsGrouped[numericStartDate][componentDuration][frequency] = price;
        } else {
          futurePaymentsGrouped[numericStartDate][componentDuration][frequency] += price;
        }
      });
    });

    return { lineItemsMap: futurePaymentsGrouped, currencyCode: currencyCode };
  }

  private convertEndDateToDuration(startDate: Date, endDate: Date, frequency: RevenuePeriod) {
    if (frequency === RevenuePeriod.ONETIME || !frequencyConversionValue[frequency]) {
      return FuturePaymentScheduledTime.NoRenewal;
    }

    const yearDifference = endDate.getFullYear() - startDate.getFullYear();
    const monthDifference = endDate.getMonth() - startDate.getMonth();
    return Math.round((yearDifference * 12 + monthDifference) / frequencyConversionValue[frequency]);
  }

  private calculateLineItemPrice(
    price: number,
    quantity: number,
    taxRateIds: string[],
    taxOptions: TaxOptions[],
  ): number {
    const tax = getTaxAmountOfLineItemForRevenuePeriodPrice(price, quantity, taxRateIds, taxOptions);
    if (!price || !quantity) return 0;
    return price * quantity + tax;
  }

  /*
  filters out line items that are not recurring.
  Recurring items are:
    - items with start date in the future
    - non-one-time items with duration > 1 or renewing indefinitely
 */
  public isLineItemRecurring(startDate: Date, dateIn24Hours: Date, duration: number, frequency: RevenuePeriod) {
    return (
      (startDate && startDate >= dateIn24Hours) ||
      ((duration > 1 || duration == FuturePaymentScheduledTime.RenewIndefinitely) &&
        frequency !== RevenuePeriod.ONETIME)
    );
  }
}
